import { useState } from "react";
import { Plus, Sparkles } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import {
  AnswerAssistantModal,
} from "./AnswerAssistantModal";
import type { AnswerAssistantAction } from "@/api/answerAssistant";

// LLM & Reports v3.2 §9.1 — surfaces the Improve / Expand buttons on
// any long-answer (TextArea) form field whose value is non-empty and
// whose viewer holds Reports.UseAnswerAssistant. The buttons live as
// siblings of the textarea inside a stable container so they survive
// the form's submit lifecycle — visibility is driven purely by the
// textarea's value, never by `submitted` / `streaming` / focus state.
//
// The submission-row dependency is deferred to button-click time:
// while the user is just typing we don't yet have a submissionId
// (the form-fill draft hasn't been saved). On first Improve / Expand
// click we call ensureSubmission() — the parent wires that to its
// existing draft-save mutation — and open the modal once the id
// resolves.

export interface AnswerAssistantButtonsProps {
  reportSubmissionId: string | null;
  questionId: string;
  value: string;
  onChange: (next: string) => void;
  /**
   * Lazy submission-creator. Called on the first Improve / Expand
   * click when `reportSubmissionId` is null; the parent's
   * implementation should save the in-progress form-fill as a draft
   * (or update the existing one) and resolve to the submission id.
   * Returning null aborts the modal-open with a toast — surfaces
   * validation failures (e.g. "Pick a template first") to the user.
   */
  ensureSubmission?: () => Promise<string | null>;
}

export function AnswerAssistantButtons({
  reportSubmissionId,
  questionId,
  value,
  onChange,
  ensureSubmission,
}: AnswerAssistantButtonsProps) {
  const { hasPermission } = useAuth();
  const { toast } = useToast();
  const canUse = hasPermission("Reports.UseAnswerAssistant");
  const [openAction, setOpenAction] = useState<AnswerAssistantAction | null>(null);
  const [openSubmissionId, setOpenSubmissionId] = useState<string | null>(null);
  // True while ensureSubmission() is in-flight. Disables the buttons
  // briefly so a double-click doesn't fire two saves; this is a
  // transient internal state, NOT a form-lifecycle gate — visibility
  // doesn't change.
  const [resolving, setResolving] = useState(false);

  // Spec §9.1: visibility is driven by text content alone. Permission
  // failures hide the buttons (they would 403 on every click).
  const showButtons = canUse && !!value && value.trim().length > 0;

  if (!showButtons) return null;

  const handleClick = async (action: AnswerAssistantAction) => {
    let id = reportSubmissionId;
    if (!id && ensureSubmission) {
      try {
        setResolving(true);
        id = await ensureSubmission();
      } catch (err) {
        toast({
          tone: "error",
          message:
            err instanceof Error
              ? err.message
              : "Couldn't save the draft before opening the assistant.",
        });
        return;
      } finally {
        setResolving(false);
      }
    }
    if (!id) {
      toast({
        tone: "info",
        message: "Save the form as a draft first so the assistant can log against it.",
      });
      return;
    }
    setOpenSubmissionId(id);
    setOpenAction(action);
  };

  return (
    <>
      <div className="mt-space-1 flex items-center gap-space-2">
        <Button
          type="button"
          variant="tertiary"
          size="sm"
          disabled={resolving}
          onClick={() => void handleClick("improve")}
        >
          <Sparkles size={14} aria-hidden /> Improve
        </Button>
        <Button
          type="button"
          variant="tertiary"
          size="sm"
          disabled={resolving}
          onClick={() => void handleClick("expand")}
        >
          <Plus size={14} aria-hidden /> Expand
        </Button>
      </div>
      {openAction && openSubmissionId && (
        <AnswerAssistantModal
          open
          action={openAction}
          reportSubmissionId={openSubmissionId}
          questionId={questionId}
          originalText={value}
          onClose={() => {
            setOpenAction(null);
            setOpenSubmissionId(null);
          }}
          onAccept={(next) => {
            onChange(next);
            setOpenAction(null);
            setOpenSubmissionId(null);
          }}
          onAppend={(addition) => {
            const joined = value.trim().length > 0 ? `${value}\n\n${addition}` : addition;
            onChange(joined);
            setOpenAction(null);
            setOpenSubmissionId(null);
          }}
        />
      )}
    </>
  );
}
